import React, { useEffect, useState } from 'react';
import * as RiIcons from 'react-icons/ri';
import * as FaIcons from 'react-icons/fa';
import { BsFilter } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import { Tabs } from 'components';
import { useMap } from 'hooks/useMap';
import { useOptions } from 'hooks/useOptions';
import ReactTooltip from 'react-tooltip';
import { Filter } from 'components/Filter';
import { Home, Layers, FavoritesLayer, Organization } from './pages';
import linksNav from './linksNav';
import * as Styled from './styles';
import { HiHome } from 'react-icons/hi';
import { DashboardLayer } from './pages/Dashboard';
import { Popup } from 'components/Popup';
import PrintMap from './pages/PrintMap';
import { useLayers } from 'hooks/useLayers';
import { UploadLayerDialog } from '../LayerDialog/index';


import { Tools } from 'screens/Map/components/SideBar/pages/Tools';
import { TbZoomInArea } from 'react-icons/tb';
import { useTabelaAtributos } from 'hooks/useTabelaAtributos';
import { useZIndex } from 'hooks/useZIndex';

interface ISideBar {
  handleSearchInputActive: () => void;
  onBaseMapModalOpen: () => void;
}

export function SideBar({ handleSearchInputActive, onBaseMapModalOpen }: ISideBar) {
  const [isActive, setIsActive] = useState<boolean>(true);
  const [tabIndex, setTabIndex] = useState<number>(1);
  const [title, setTitle] = useState('Início');
  const [iconTitleTab, setIconTitleTab] = useState<React.ReactNode>(<HiHome />);
  const { subThemeGraph, layersActived } = useLayers();

  const [openUpload, setOpenUpload] = useState(false);
  const { t: translation, i18n } = useTranslation();
  const { openMap } = useOptions();
  const { failedLoadTile, setFailedLoadTile, handleFitExtent, handleChangeTool, selectedTool } = useMap();
  const { bringToFront, getZIndex } = useZIndex();

  const { openFilter, setOpenFilter } = useTabelaAtributos();

  const [dataBreadcrumb, setDataBreadcrumb] = useState<{ label: string; action?: () => void }[]>([
    {
      label: translation('mapPage.sideBar.navigationBottomBar.home'),
      action: () => handleChangeTabIndex(1, 'Início', <HiHome />),
    },
  ]);

  useEffect(() => {
    if (selectedTool) handleChangeTool(selectedTool);
  }, [tabIndex])
  

  const addBreadcrumb = (itemAddBreadcrumb: { label: string; action?: () => void }, subBreadcrumbs?: string[]) => {
    const labelsBreadcrumb = dataBreadcrumb.map(({ label }) => label);

    const hasSubBreadcrumbs = subBreadcrumbs ? subBreadcrumbs.map(label => ({ label })) : [];

    if (labelsBreadcrumb.includes(itemAddBreadcrumb.label)) {
      const index = labelsBreadcrumb.indexOf(itemAddBreadcrumb.label);
      const newBreadcrumb = dataBreadcrumb.slice(0, index + 1);
      setDataBreadcrumb([...newBreadcrumb, ...hasSubBreadcrumbs]);
    } else {
      setDataBreadcrumb([...dataBreadcrumb, itemAddBreadcrumb, ...hasSubBreadcrumbs]);
    }
  };

  const handleChangeTabIndex = (index: number, text: string, icon: React.ReactNode, subBreadcrumbs?: string[]) => {
    setIsActive(true);
    setTabIndex(index);
    setTitle(translation(text.split('-')[0]));
    setIconTitleTab(icon);

    addBreadcrumb(
      {
        label: translation(text),
        action: () => handleChangeTabIndex(index, text, icon),
      },
      subBreadcrumbs,
    );
  };

  useEffect(() => {
    if (openMap) {
      setIsActive(true);
    }
  }, [openMap]);

  useEffect(() => {
    if (failedLoadTile) {
      setTimeout(() => setFailedLoadTile(false), 5000);
    }
  }, [failedLoadTile]);

  const toggleSidebar = () => {
    setIsActive(!isActive);
  };

  const handleActivate = () => {
    bringToFront('map-sidebar');
  };

  return (
    <Styled.Wrapper
      actived={isActive}
      isEnUS={i18n.language === 'en-US'}
      zIndex={getZIndex('map-sidebar')}
    >
      {failedLoadTile && (
        <Popup text="Não foi encontrado registro para o(s) filtro(s) informado(s)!" animation={failedLoadTile} />
      )}
      <Styled.Content actived={isActive} onMouseDown={handleActivate} onTouchStart={handleActivate}>
        <Tabs
          title={title}
          options={linksNav}
          onChange={handleChangeTabIndex}
          actived={tabIndex}
          iconTitle={iconTitleTab}
          breadcrumb={dataBreadcrumb}
          showClearAll={[1, 2, 3].includes(tabIndex)}
          tabIndex={tabIndex}
        >
          <Home handleChangeTabIndex={handleChangeTabIndex} />
          <Layers handleChangeTabIndex={handleChangeTabIndex} />
          <DashboardLayer />
          <Organization />
          <FavoritesLayer handleChangeTabIndex={handleChangeTabIndex} />
          <Filter
            mapFilter
            applyFilter={async () => {
              handleFitExtent();
            }}
          />
          <Tools />
        </Tabs>
      </Styled.Content>
      <Styled.NavigationBar onMouseDown={handleActivate} onTouchStart={handleActivate}>
        <header>
          <button type="button" className="expandedButton" onClick={toggleSidebar}>
            <RiIcons.RiArrowRightSLine size={38} color="#FFF" />
          </button>
        </header>
        <div className="buttonsBlock">
          <span>
            <button
              type="button"
              className="buttonLink"
              onClick={() => {
                setIsActive(false);
                handleSearchInputActive();
              }}
            >
              <div className="icons-sidebar">
                <RiIcons.RiSearchLine />
              </div>
              <span>{translation('mapPage.sideBar.navigationRightBar.quickSearch')}</span>
            </button>
          </span>
          <span>
            <button
              type="button"
              className="buttonLink"
              onClick={() => {
                handleChangeTabIndex(5, 'Favoritos', <FaIcons.FaRegStar />);
              }}
            >
              <div className="icons-sidebar">
                <FaIcons.FaRegStar />
              </div>
              <span>{translation('mapPage.sideBar.navigationRightBar.favorites')}</span>
            </button>
          </span>
          <span>
            <button
              type="button"
              className="buttonLink"
              onClick={() => {
                handleChangeTabIndex(6, 'Recorte Territorial', <TbZoomInArea />);
              }}
            >
              <div className="icons-sidebar">
                <TbZoomInArea />
              </div>
              <span>{translation('mapPage.sideBar.navigationRightBar.recorte')}</span>
            </button>
          </span>
          <span>
            <button
              type="button"
              className="buttonLink"
              disabled={layersActived.length === 0}
              onClick={() => {
                setOpenFilter(!openFilter);
              }}
            >
              <div className="icons-sidebar">
                <BsFilter />
              </div>
              <span>{translation('mapPage.sideBar.navigationRightBar.filter')}</span>
            </button>
          </span>
          <div className="toolRow">
            <button
              type="button"
              className="buttonLink"
              onClick={() => handleChangeTabIndex(7, 'Ferramentas', <RiIcons.RiToolsFill />)}
            >
              <div className="icons-sidebar">
                <RiIcons.RiToolsFill />
              </div>
              <span>{translation('mapPage.sideBar.navigationRightBar.tools')}</span>
            </button>
          </div>
          <span>
            <button
              type="button"
              className="buttonLink"
              onClick={() => {
                setIsActive(false);
                onBaseMapModalOpen();
              }}
            >
              <div className="icons-sidebar">
                <RiIcons.RiMap2Line />
              </div>
              <span>{translation('mapPage.sideBar.navigationRightBar.settings')}</span>
            </button>
          </span>
          <PrintMap />
          <span>
            <button
              type="button"
              className="buttonLink"
              onClick={() => setOpenUpload(true)}
            >
              <div className="icons-sidebar">
                <RiIcons.RiUploadCloud2Line />
              </div>
              <span>Upload de camada</span>
            </button>
          </span>
          <ReactTooltip id="settingsButton" place="top" type="warning" effect="solid" delayShow={500} delayHide={500}>
            <p>{translation('mapPage.sideBar.navigationRightBar.functionalDev')}</p>
          </ReactTooltip>
          <ReactTooltip id="graphicsButton" place="top" type="warning" effect="solid" delayShow={500} delayHide={500}>
            <p>
              {!!subThemeGraph.nm_subtema
                ? subThemeGraph.nm_subtema
                : translation('mapPage.sideBar.navigationRightBar.graphicsLabel')}
            </p>
          </ReactTooltip>
        </div>
      </Styled.NavigationBar>
      <UploadLayerDialog open={openUpload} onClose={() => setOpenUpload(false)} />
    </Styled.Wrapper>
  );
}
